/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Acima FPP-in-restricted-ZIP eligibility helper.
 *
 * Legacy parity for the `handyFppRestrictionsInStatesWithAcimaPayment` site preference
 * (shape: `{"zipcodePrefixes": ["460-479", ...]}`). When the shipping ZIP falls in one
 * of the configured 3-digit prefix ranges AND the basket contains a Furniture
 * Protection Plan (FPP), the Acima payment radio at checkout renders an inline
 * warning instead of the normal Sign Lease iframe. Server-side, the Custom SCAPI
 * `GET /checkout-details` endpoint additionally rejects such requests with fault
 * type `acima-fpp-restricted-zip` as defense-in-depth.
 *
 * The FE receives the `zipcodePrefixes` array as `fppRestrictedZipPrefixes` on
 * `AcimaCheckoutConfig` — each entry is an inclusive 3-digit range in "min-max"
 * form. Malformed entries are silently skipped: a config typo must never
 * over-restrict a shopper.
 *
 * Kept in a non-component file so React Fast Refresh doesn't flag the helpers.
 */

/**
 * Extracts the 3-digit prefix from a 5-digit US ZIP. Returns `null` for anything
 * that doesn't look like a US ZIP (empty, short, non-numeric prefix).
 */
export function toZipPrefix(zip: string | null | undefined): string | null {
    if (!zip) return null;
    // Strip whitespace and ZIP+4 suffix. `12345-6789` → `12345`.
    const compact = String(zip).trim().split('-')[0]?.trim();
    if (!compact || compact.length < 3) return null;
    const prefix = compact.slice(0, 3);
    if (!/^\d{3}$/.test(prefix)) return null;
    return prefix;
}

/**
 * Parses a single "min-max" range spec into numeric bounds. Returns `null` for
 * malformed specs (missing separator, wrong digit count, non-numeric, min > max).
 * Malformed entries are silently skipped by callers to preserve fail-open
 * semantics — a merchant typo must not over-restrict shoppers.
 */
export function parseRangeSpec(spec: string): { minNum: number; maxNum: number } | null {
    if (typeof spec !== 'string') return null;
    const parts = spec.split('-');
    if (parts.length !== 2) return null;
    const minStr = parts[0]?.trim();
    const maxStr = parts[1]?.trim();
    if (!minStr || !maxStr) return null;
    if (!/^\d{3}$/.test(minStr) || !/^\d{3}$/.test(maxStr)) return null;
    const minNum = Number(minStr);
    const maxNum = Number(maxStr);
    if (!Number.isFinite(minNum) || !Number.isFinite(maxNum) || minNum > maxNum) return null;
    return { minNum, maxNum };
}

/**
 * True when `zip`'s 3-digit prefix falls within any of the configured range specs.
 * Comparison is numeric on the 3-digit prefix so `900 ≤ 902 ≤ 961` works correctly.
 * Malformed specs (non-numeric, min > max, wrong shape) are skipped silently — an
 * invalid pref value must NOT accidentally over-restrict.
 */
export function isZipInAnyPrefix(zip: string | null | undefined, prefixes: string[]): boolean {
    const prefix = toZipPrefix(zip);
    if (!prefix) return false;
    if (!Array.isArray(prefixes) || prefixes.length === 0) return false;
    const zipNum = Number(prefix);
    if (!Number.isFinite(zipNum)) return false;
    return prefixes.some((spec) => {
        const range = parseRangeSpec(spec);
        if (!range) return false;
        return zipNum >= range.minNum && zipNum <= range.maxNum;
    });
}

/**
 * Full FPP+ZIP restriction check. Returns true when Acima MUST be blocked for this
 * shopper — that is, both conditions hold:
 *   1. The basket contains at least one protection-plan item (`c_isProtectionPlan`).
 *   2. The shipping ZIP falls in one of the configured restricted prefix ranges.
 *
 * The caller is expected to derive `hasFpp` from the basket's product items and
 * `shippingZip` from the selected shipping address.
 */
export function isAcimaFppZipRestricted(
    hasFpp: boolean,
    shippingZip: string | null | undefined,
    prefixes: string[]
): boolean {
    if (!hasFpp) return false;
    return isZipInAnyPrefix(shippingZip, prefixes);
}
